import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { BaseService } from '../../../../../services/base.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GroupForm, GroupPermission, Permission, PERMISSIONS, PERMISSIONS_KEYS } from './create-group.interface';
import { Subject, takeUntil } from 'rxjs';
import { CrmService } from '../../../../../services/crm.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { IError } from '../../../../../interfaces';
import { CHANNEL_TYPE, CHANNEL_TYPES_OBJECT, ERROR_MESSAGES, activeTranslations } from '../../../../../enum/constants';
import { DialogService } from '../../../../../services/dialog.service';
import * as _ from 'lodash';
import { trimValidator } from '../../../../../helpers';
import { CHIP_TYPES } from '../../../../chip/chip.interface';
import { SelectType } from '../../../../select/select.interface';

@Component({
  selector: 'app-create-group',
  templateUrl: './create-group.component.html',
  styleUrl: './create-group.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateGroupComponent implements OnInit, OnDestroy {
  // Imports
  protected readonly PERMISSIONS = PERMISSIONS;
  protected readonly CHIP_TYPES = CHIP_TYPES;

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  // Destroy
  private readonly destroy$ = new Subject<null>();

  // Form
  public groupForm!: FormGroup<GroupForm>;

  // Var`s
  private priority = [
    'CRM',
    'COMPANY',
    'PAYMENTS',
    'TEAM',
    'INTEGRATION',
    'CHANNELS',
    'CHATS',
    'APPS',
    'SETTINGS',
    'CONTACTS',
    'LOGISTICS',
    'FILES',
    'AUTOMATION',
    'IP',
  ];

  public initialValue: any;
  public group: any;
  public group_id!: string;

  public permissions: Permission[] = [];
  public groupPermissions: string[] = [];
  public groupedPermissions: { type: string; permissions: Permission[] }[] = [];
  public startGroupPermissions: string[] = [];
  public isLoaderPermissions: boolean = false;
  public isHasChange: boolean = false;
  public isLoader: boolean = false;

  constructor(
    private readonly crmService: CrmService,
    public readonly baseService: BaseService,
    public readonly dialogService: DialogService,
    private readonly notificationService: NotificationService,
  ) {}

  public ngOnInit(): void {
    this.group_id = this.payload?.data?.group_id;
    this.isLoader = true;
    this.isLoaderPermissions = true;
    this.onGetAllPermissions();
    if (this.group_id) {
      this.onGetGroupById();
      this.onGetAllPermissionsForGroup();
    } else {
      this.initForm();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private initForm(): void {
    this.groupForm = new FormGroup({
      name: new FormControl(this.group?.name || '', [Validators.required, trimValidator]),
      x_data: new FormGroup({}),
    });
    this.isLoader = false;
    this.onCreateGroupFormValueChange();
  }

  public onCloseSidebar(): void {
    this.close.emit('close');
  }

  public onCreateGroup(): void {
    this.groupForm.markAllAsTouched();

    if (this.groupForm.valid) {
      const { name, x_data } = this.groupForm.getRawValue();
      this.crmService
        .createGroup({
          name,
          permissions: this.groupPermissions,
          x_data,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.close.emit('success');
          },
          error: (e: any) => {
            (e.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[err.code]);
            });
          },
        });
    }
  }

  public onUpdateGroup(): void {
    if (this.groupForm.valid) {
      const { name, x_data } = this.groupForm.getRawValue();
      this.crmService
        .updateGroup(
          {
            name,
            permissions: this.groupPermissions,
            x_data: x_data,
          },
          this.group_id,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.close.emit('success');
          },
          error: (e: any) => {
            (e.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[err.code]);
            });
          },
        });
    }
  }

  public onGetAllPermissions(): void {
    this.crmService
      .getAllPermissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (v: any) => {
          this.permissions = v;
          this.groupPermissionsByType();
          if (!this.group_id) {
            this.groupPermissions = [...this.permissions.map((v: Permission) => v.id)];
            this.isLoaderPermissions = false;
          }
        },
        error: (e: any) => {
          this.isLoaderPermissions = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
  }

  public onGetAllPermissionsForGroup(): void {
    this.crmService
      .getAllPermissionsByGroupId(this.group_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (v: any) => {
          this.groupPermissions = [...v.map((v: GroupPermission) => v.permission_id)];
          this.startGroupPermissions = [...this.groupPermissions];
          this.isLoaderPermissions = false;
        },
        error: (e: any) => {
          this.isLoaderPermissions = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
  }

  public onSelectRadio(e: any, permission: Permission): void {
    if (e && !this.groupPermissions.includes(permission.id)) this.groupPermissions.push(permission.id);
    if (!e && this.groupPermissions.includes(permission.id))
      this.groupPermissions = this.groupPermissions.filter((v: string) => v !== permission.id);

    this.checkIsHasChange();
  }

  public onSelectRadioAll(e: any): void {
    if (e.value === 1) this.groupPermissions = [...this.permissions].map((v) => v.id);
    if (e.value === 0) this.groupPermissions = [];

    this.checkIsHasChange();
  }

  public onGetGroupById(): void {
    this.crmService
      .getGroupById(this.group_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (v: any) => {
          this.group = v;
          this.initForm();
        },
        error: (e: any) => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
  }

  public onDeleteGroup(): void {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.create_group',
    });

    dialogRef.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res) {
          this.crmService
            .deleteGroup(this.group_id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.notificationService.showSuccess(
                  activeTranslations.notifications_texts.success.successfully_deleted,
                  '',
                );
                this.close.emit('success');
              },
              error: (e: any) => {
                (e.error?.errors || []).forEach((err: IError) => {
                  this.notificationService.showError('', ERROR_MESSAGES[err.code]);
                });
              },
            });
        }
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code]);
        });
      },
    });
  }

  private onCreateGroupFormValueChange(): void {
    const initialValue = { ...this.groupForm.getRawValue() };
    this.initialValue = initialValue;

    this.groupForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkIsHasChange();
    });
  }

  private checkIsHasChange(): void {
    this.isHasChange =
      !_.isEqual(this.groupPermissions, this.startGroupPermissions) ||
      !_.isEqual(this.groupForm.getRawValue(), this.initialValue);
  }

  private groupPermissionsByType(): void {
    const grouped = this.permissions.reduce(
      (acc, permission) => {
        const type = permission.type;
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(permission);
        return acc;
      },
      {} as Record<string, Permission[]>,
    );

    const groupedPermissions = Object.keys(grouped).map((type) => ({
      type,
      permissions: grouped[type],
    }));

    this.groupedPermissions = groupedPermissions.sort((a, b) => {
      return this.priority.indexOf(a.type) - this.priority.indexOf(b.type);
    });
  }

  protected readonly PERMISSIONS_KEYS = PERMISSIONS_KEYS;
  protected readonly SelectType = SelectType;
}
